import APIService from "./APIService";

/**
 * API service for user appearance/display settings.
 * Settings are persisted on the backend and cached in localStorage for fast access.
 */
const UserSettingsAPI = {
  BASE_PATH: "/api/v1/app/user-settings",

  /**
   * Get the authenticated user's settings from the backend.
   * Falls back to defaults if the request fails.
   *
   * @returns {Promise<Object>} Settings object
   */
  getSettings: async () => {
    try {
      const data = await APIService.get(UserSettingsAPI.BASE_PATH);
      if (data) {
        // Sync backend settings into localStorage cache
        UserSettingsAPI._syncToLocalStorage(data);
      }
      return data;
    } catch (error) {
      console.error("UserSettingsAPI: Error fetching settings:", error);
      return null;
    }
  },

  /**
   * Update one or more settings on the backend (partial update).
   * Also updates the localStorage cache.
   *
   * @param {Object} settings - Fields to update (only non-null fields are applied)
   * @returns {Promise<Object>} Updated settings object
   */
  updateSettings: async (settings) => {
    try {
      const data = await APIService.put(UserSettingsAPI.BASE_PATH, settings);
      if (data) {
        UserSettingsAPI._syncToLocalStorage(data);
      }
      return data;
    } catch (error) {
      console.error("UserSettingsAPI: Error updating settings:", error);
      throw error;
    }
  },

  /**
   * Sync backend settings object into localStorage keys
   * so that existing localStorage-based reads (Hero.js, LoginFormModal, etc.)
   * continue to work as a fast cache.
   *
   * @param {Object} settings - Backend settings object
   */
  _syncToLocalStorage: (settings) => {
    if (!settings) return;

    // Dashboard & UI
    if (settings.dashboardView != null) {
      localStorage.setItem("dashboardView", settings.dashboardView);
    }
    if (settings.dashboardMode != null) {
      localStorage.setItem("dashboardMode", settings.dashboardMode);
    }
    if (settings.showViewSettings != null) {
      localStorage.setItem(
        "showViewSettings",
        settings.showViewSettings.toString(),
      );
    }
    if (settings.defaultLandingPage != null) {
      localStorage.setItem("landingPage", settings.defaultLandingPage);
    }
    if (settings.themePreference != null) {
      localStorage.setItem("theme", settings.themePreference);
    }

    // Notifications
    if (settings.emailSecurityAlerts != null) {
      localStorage.setItem(
        "emailSecurityAlerts",
        settings.emailSecurityAlerts.toString(),
      );
    }
    if (settings.emailTrainingReminders != null) {
      localStorage.setItem(
        "emailTrainingReminders",
        settings.emailTrainingReminders.toString(),
      );
    }
    if (settings.emailWeeklyDigest != null) {
      localStorage.setItem(
        "emailWeeklyDigest",
        settings.emailWeeklyDigest.toString(),
      );
    }
    if (settings.emailMarketing != null) {
      localStorage.setItem(
        "emailMarketing",
        settings.emailMarketing.toString(),
      );
    }
    if (settings.inappAlertLevel != null) {
      localStorage.setItem("alertLevel", settings.inappAlertLevel);
    }
    if (settings.pushNotificationsEnabled != null) {
      localStorage.setItem(
        "pushNotificationsEnabled",
        settings.pushNotificationsEnabled.toString(),
      );
    }

    // Security
    if (settings.loginNotificationsEnabled != null) {
      localStorage.setItem(
        "loginNotificationsEnabled",
        settings.loginNotificationsEnabled.toString(),
      );
    }

    // Localization
    if (settings.language != null) {
      localStorage.setItem("language", settings.language);
    }
    if (settings.timezone != null) {
      localStorage.setItem("timezone", settings.timezone);
    }
    if (settings.dateFormat != null) {
      localStorage.setItem("dateFormat", settings.dateFormat);
    }
    if (settings.timeFormat != null) {
      localStorage.setItem("timeFormat", settings.timeFormat);
    }

    // Privacy
    if (settings.analyticsEnabled != null) {
      localStorage.setItem(
        "analyticsEnabled",
        settings.analyticsEnabled.toString(),
      );
    }
    if (settings.profileVisibility != null) {
      localStorage.setItem("profileVisibility", settings.profileVisibility);
    }
  },
};

export default UserSettingsAPI;
